import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import { postAppointmentCheckIn } from "../../../apis/appointmentApi";
import { AppContext } from "../../../context/app";
import { defaultToastOptions, extractError } from "../../../utils/misc";

interface ContextValue {
  checkInAppointment: (
    appointmentUuid: string,
    options: Options,
  ) => Promise<void>;
}

interface AppointmentCheckInProviderProps {
  children?: ReactNode;
}

interface Options {
  onSuccess?: VoidFunction;
  onError?: (error: unknown) => void;
}

const Context = createContext<ContextValue | undefined>(undefined);

function AppointmentCheckInProvider(props: AppointmentCheckInProviderProps) {
  const { children } = props;

  const app = useContext(AppContext);

  const checkInMutation = useMutation(postAppointmentCheckIn);

  const checkInAppointment = useCallback(
    async (uuid: string, options: Options = {}) => {
      try {
        await checkInMutation.mutateAsync({
          clientUuid: app.client!.uuid,
          appointmentId: uuid,
        });
        toast.success("Checked in", defaultToastOptions);
        options?.onSuccess?.();
      } catch (error) {
        toast.error(extractError(error), defaultToastOptions);
        options?.onError?.(error);
      }
    },
    [app.client, checkInMutation],
  );

  const contextValue = useMemo(() => {
    return { checkInAppointment };
  }, [checkInAppointment]);

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

export function useAppointmentCheckIn() {
  const contextValue = useContext(Context);
  if (!contextValue) {
    throw new Error(
      "useAppointmentCheckIn must to be used inside an AppointmentCheckInProvider",
    );
  }
  return contextValue;
}

export default AppointmentCheckInProvider;
